import React, { useRef, useState, useEffect } from 'react';
import CrossArrow from '../../svg/CrossArrow';
import PlayCircle from '../../svg/PlayCircle';
import Play from '../../svg/Play';
import Pause from '../../svg/Pause';
import More from '../../svg/More';
import { getContrastColor } from '../DizeeComponent/DixeeInput2';

export default function Video(props) {
    const { userArticle, video, setOverlay, title } = props;
    const scrollRef = useRef(null);
    const videoRefs = useRef([]);
    const [arrowsVisible, setArrowsVisible] = useState({ left: false, right: true });
    const [playingIndex, setPlayingIndex] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);

    const scrollLeft = () => {
        scrollRef.current?.scrollBy({ left: -318, behavior: 'smooth' });
    };

    const scrollRight = () => {
        scrollRef.current?.scrollBy({ left: 318, behavior: 'smooth' });
    };

    const updateArrowVisibility = () => {
        if (scrollRef.current) {
            const scrollLeft = scrollRef.current.scrollLeft;
            const scrollWidth = scrollRef.current.scrollWidth;
            const clientWidth = scrollRef.current.clientWidth;

            setArrowsVisible({
                left: scrollLeft > 0,
                right: scrollLeft + clientWidth < scrollWidth
            });

            const index = Math.round(scrollLeft / (318 + 25));
            setCurrentIndex(index);
        }
    };

    useEffect(() => {
        const container = scrollRef.current;
        if (!container) return;

        updateArrowVisibility();
        container.addEventListener('scroll', updateArrowVisibility);
        return () => container.removeEventListener('scroll', updateArrowVisibility);
    }, []);

    const handleVideoClick = (index) => {
        const videoElement = videoRefs.current[index];
        if (videoElement) {
            if (videoElement.paused) {
                videoElement.play();
                setPlayingIndex(index);
            } else {
                videoElement.pause();
                setPlayingIndex(null);
            }
        }

        videoRefs.current.forEach((v, i) => {
            if (i !== index && v) v.pause();
        });
    };

    const getVideoId = (url) => {
        if (!url) return null;
        try {
            const cleanUrl = url.trim();
            if (cleanUrl.includes('youtube.com/shorts/')) {
                return cleanUrl.split('/shorts/')[1].split('?')[0];
            }
            if (cleanUrl.includes('youtu.be/')) {
                return cleanUrl.split('youtu.be/')[1].split('?')[0];
            }
            if (cleanUrl.includes('youtube.com/watch')) {
                const urlObj = new URL(cleanUrl);
                return urlObj.searchParams.get("v");
            }
            return null;
        } catch (e) {
            console.error("Invalid URL:", url);
            return null;
        }
    };

    return (
        <div className='w-full px-4 mb-[60px] relative'>
            {/* Header */}
            <div className="text-[#FDFAFA] my-4 flex justify-between items-center">
                <p style={userArticle?.accent ? { color: `#${userArticle?.accent}` } : { color: '#ffffff' }}>
                    {title || 'VIDEO'}
                </p>
                <More
                    className='h-[20px] w-[20px] cursor-pointer'
                    color={getContrastColor(userArticle?.background) || '#ffffff'}
                    onClick={() => setOverlay(true)}
                />
            </div>

            {/* Video and Scroll Arrows */}
            <div className='relative'>
                <div className='w-full overflow-x-auto' ref={scrollRef}>
                    <div className='flex gap-x-[25px]'>
                        {video?.map((item, index) => {
                            const videoId = getVideoId(item?.links?.[0]?.url);
                            return (
                                <div key={index} className='flex-shrink-0 w-[318px] relative'>
                                    {item?.type === 0 && videoId ? (
                                        <iframe
                                            className='object-cover h-[460px] w-full'
                                            src={`https://www.youtube.com/embed/${videoId}`}
                                            frameBorder="0"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                            title={`youtube-${index}`}
                                        />
                                    ) : (
                                        <>
                                            <video
                                                ref={el => videoRefs.current[index] = el}
                                                src={item?.avatar}
                                                className="object-cover h-[460px] w-full"
                                                muted
                                                controls={false}
                                                onClick={() => handleVideoClick(index)}
                                            />
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleVideoClick(index);
                                                }}
                                                className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                                            >
                                                {playingIndex === index ? (
                                                    <Pause className='h-[40px] w-[40px] text-white' />
                                                ) : (
                                                    <PlayCircle className='h-[40px] w-[40px]' />
                                                )}
                                            </button>
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Left Scroll Arrow */}
                {arrowsVisible.left && (
                    <div onClick={scrollLeft} className='absolute bottom-4 left-0 p-2 cursor-pointer z-10'>
                        <Play className='rotate-180' color={getContrastColor(userArticle?.background) || '#ffffff'} />
                    </div>
                )}

                {/* Right Scroll Arrow */}
                {arrowsVisible.right && (
                    <div onClick={scrollRight} className='absolute bottom-4 right-0 p-2 cursor-pointer z-10'>
                        <Play color={getContrastColor(userArticle?.background) || '#ffffff'} />
                    </div>
                )}

                {/* Fixed Slider Dots (not scrollable) */}
                {video?.length > 1 && (
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-x-2 z-20">
                        {video.map((_, index) => (
                            <div
                                key={index}
                                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                                    currentIndex === index ? 'bg-white' : 'bg-gray-400'
                                }`}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}